// RULING 23 — debit note certification. Configuration only; the shape is CertificationQueue's.
//
// A debit note REDUCES a payable, so it is the mirror of a purchase invoice and the queue shows the
// invoice it reduces alongside it — including that invoice's own certification state, because a debit
// note against an uncertified invoice is more interesting, not less.
//
// AND EVERY ROW HERE IS ACTIONABLE, unlike the sales queue. All 248 debit notes were raised by
// personal logins, so ruling 33 blocks none of them. This is the first of the three approval acts
// that anybody can actually use on the day it ships.
import React from 'react';
import CertificationQueue from './CertificationQueue';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const CONFIG = {
  queueUrl: '/finance/debit-notes/certification-queue',
  certifyUrl: (id) => `/finance/debit-notes/${id}/certify`,
  title: '✅ Debit Note Certification',
  blurb: "Finance's verdict that the debit note correctly reduces what we owe. A debit note is the mirror of a purchase invoice, so it takes the mirror control.",
  noun: 'debit note', aNoun: 'a debit note', nounPlural: 'debit notes',
  lockNote: '',
  emptyText: 'Nothing awaiting certification.',
  refOf: (r) => r.debit_no, amountOf: (r) => r.debit_note_total,
  columns: [
    { key: 'dn', label: 'Debit Note', render: (r) => (<>
        <div style={{ fontWeight: 600 }}>{r.debit_no || <span style={{ color: '#aaa' }}>(no number)</span>}</div>
        {r.invoice_no && <div style={{ fontSize: 11, color: '#888' }}>vs {r.invoice_no}</div>}
      </>) },
    { key: 'date', label: 'Date', nowrap: true, render: (r) => fmtD(r.debit_date) },
    // THE RAW SUPPLIER TEXT, uncleaned. The spelling is the signal — sixth instance of
    // identity-as-free-text, and the screen shows it rather than tidying it.
    { key: 'supplier', label: 'Supplier', render: (r) => (
        <div style={{ fontFamily: r.supplier_raw ? 'monospace' : 'inherit', fontSize: 12 }}>
          {r.supplier_raw || <span style={{ color: '#9B1C1C' }}>not recorded</span>}
        </div>) },
    { key: 'amt', label: 'Debited', right: true, render: (r) => (<>
        <div style={{ fontWeight: 600 }}>{inr(r.debit_note_total)}</div>
        <div style={{ fontSize: 10.5, color: '#888' }}>
          {[num(r.shortage_value) > 0 && `short ${inr(r.shortage_value)}`,
            num(r.rejection_value) > 0 && `reject ${inr(r.rejection_value)}`,
            num(r.delay_value) > 0 && `delay ${inr(r.delay_value)}${r.delay_days ? ` (${r.delay_days}d)` : ''}`]
            .filter(Boolean).join(' · ') || '—'}
        </div>
      </>) },
  ],
  // THE INVOICE IT REDUCES, with that invoice's own verdict. A debit note certified against an
  // uncertified invoice is a real state and is shown rather than hidden.
  extraColumns: [
    { key: 'inv', label: 'Invoice', render: (r) => (<>
        <div style={{ fontSize: 12 }}>{r.invoice_amount != null ? inr(r.invoice_amount) : '—'}</div>
        {r.invoice_review_status && (
          <div style={{ fontSize: 10.5, color: r.invoice_review_status === 'approved' ? '#1E6B33' : '#8A5A12' }}>
            {r.invoice_review_status === 'approved' ? 'certified' : r.invoice_review_status}
          </div>)}
      </>) },
  ],
};

export default function DebitNoteCertification() { return <CertificationQueue config={CONFIG} />; }
